import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { fetchReservation } from "@/api/reservations";
import RouteMap from "./RouteMap";
import BookingDetailHeader from "./components/BookingDetailHeader";
import WeddingScheduleCard from "./components/WeddingScheduleCard";
import TourTimeline from "./components/TourTimeline";
import SummaryPanel from "./components/SummaryPanel";
import type { BookingDetailUI, BookingDetailSlot } from "./booking-detail.types";
import DressTourTimeLine from "@/components/combinations/DressTourTimeLine";
import RouteNaverMap from "@/components/common/NaverMap/RouteNaverMap";
import { formatPhone } from "@/utils/string";
import { cancelReservation, changeCustomerName } from "@/api/planner";
import { buildDressTourNoticeText, copyToClipboard } from "@/utils/clipboard";
import { GA_EVENT, trackEvent } from "@/utils/gaUtil";

type ReservationStatus = "BOOKED" | "CANCELED";

interface ShopItem {
  id: number;
  shopName: string;
  address?: string;
  lat?: number;
  lng?: number;
  lon?: number;
}

interface ReservationDetailResponse {
  reservation: {
    id: number;
    reservationCode: string;
    bookingPlannerId: number;
    customerName: string;
    customerPhone: string;
    reservationStatus: ReservationStatus;
    reservedTime: string;
    canceledTime: string | null;
    cancelReason: string | null;
    memo: string | null;
    noShooting: number | boolean;
    shootingDate: string | null;
    weddingDate: string | null;
    notice: string | null;
    shootingDateUndecided: number | boolean;
    weddingDateUndecided: number | boolean;
  };
  items: Array<{
    reservationOpenSlotId: number;
    openSlotId: number;
    mappingStatus: "BOOKED" | "CANCELED";
    mappingCanceledTime: string | null;
    mappingCancelReason: string | null;
    slot: {
      id: number;
      shopId: number;
      slotDate: string;
      roomId: number;
      startMinuteOfDay: number;
      endMinuteOfDay: number;
      startTimeCode: number;
      endTimeCode: number;
      slotStatus: "OPEN" | "CLOSED" | "BOOKED";
      sessionMinutes: number;
      slotUnitMinutes: number;
      bookingTime: string | null;
      memo: string | null;
    };
  }>;
}
function CardSection({
  title,
  icon,
  right,
  children,
}: {
  title: string;
  icon: string;
  right?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section
      className="bg-white overflow-hidden rounded-2xl border overflow-hidden"
      style={{ borderColor: "#EEF2F7" }}
    >
      <div
        className="px-5 py-4 border-b flex items-center justify-between gap-3"
        style={{ borderColor: "#F3F4F6", backgroundColor: "#FCFCFD" }}
      >
        <div className="flex items-center gap-2">
          <i className={`${icon} text-sm`} style={{ color: "#2563EB" }}></i>
          <span className="text-sm font-semibold" style={{ color: "#374151" }}>
            {title}
          </span>
        </div>
        {right}
      </div>
      <div className="px-5 py-5">{children}</div>
    </section>
  );
}
function NotFoundState() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-5" style={{ backgroundColor: "#F5F6F8" }}>
      <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: "#F3F4F6" }}>
        <i className="ri-file-unknow-line text-2xl" style={{ color: "#D1D5DB" }}></i>
      </div>
      <p className="text-sm font-semibold mb-1" style={{ color: "#374151" }}>예약을 찾을 수 없습니다</p>
      <p className="text-xs mb-6" style={{ color: "#9CA3AF" }}>예약 코드를 다시 확인해주세요</p>
      <button
        onClick={() => navigate("/reservations")}
        className="px-5 py-2.5 rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap"
        style={{ backgroundColor: "#2563EB", color: "#FFFFFF" }}
      >
        예약 내역으로
      </button>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#F5F6F8" }}>
      <div className="text-center">
        <div
          className="w-10 h-10 mx-auto mb-3 border-2 border-t-transparent rounded-full animate-spin"
          style={{ borderColor: "#2563EB transparent #2563EB #2563EB" }}
        />
        <p className="text-sm" style={{ color: "#6B7280" }}>
          예약 정보를 불러오는 중입니다
        </p>
      </div>
    </div>
  );
}

function CancelModal({ onConfirm, onClose }: { onConfirm: (reason: string) => void; onClose: () => void }) {
  const [reason, setReason] = useState("");
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" style={{zIndex: 100}}>
      <div className="absolute inset-0" style={{ backgroundColor: "rgba(0,0,0,0.4)" }} onClick={onClose}></div>
      <div className="relative w-full sm:max-w-sm bg-white rounded-t-2xl sm:rounded-2xl px-5 pt-5 pb-8 z-10">
        <div className="w-10 h-1 rounded-full mx-auto mb-5 sm:hidden" style={{ backgroundColor: "#E5E7EB" }}></div>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "#FEF2F2" }}>
            <i className="ri-close-circle-line text-base" style={{ color: "#DC2626" }}></i>
          </div>
          <div>
            <p className="text-sm font-bold" style={{ color: "#111827" }}>예약을 취소하시겠어요?</p>
            <p className="text-xs mt-0.5" style={{ color: "#9CA3AF" }}>취소 후에는 되돌릴 수 없습니다</p>
          </div>
        </div>
        <div className="mb-4">
          <label className="block text-xs font-medium mb-1.5" style={{ color: "#374151" }}>
            취소 사유 <span style={{ color: "#9CA3AF" }}>(선택)</span>
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="취소 사유를 입력해주세요"
            maxLength={200}
            rows={3}
            className="w-full rounded-xl px-3 py-2.5 text-sm resize-none outline-none"
            style={{ border: "1px solid #E5E7EB", color: "#111827", backgroundColor: "#FAFAFA" }}
          />
          <p className="text-xs text-right mt-1" style={{ color: "#D1D5DB" }}>{reason.length}/200</p>
        </div>
        <div className="flex gap-2.5">
          <button
            onClick={onClose}
            className="flex-1 py-3.5 rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap"
            style={{ backgroundColor: "#F3F4F6", color: "#374151" }}
          >
            돌아가기
          </button>
          <button
            onClick={() => onConfirm(reason)}
            className="flex-1 py-3.5 rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap"
            style={{ backgroundColor: "#DC2626", color: "#FFFFFF" }}
          >
            예약 취소
          </button>
        </div>
      </div>
    </div>
  );
}
function NameEditModal({ onConfirm, onClose, defaultCustomerName }: { onConfirm: (customerName: string) => void; onClose: () => void; defaultCustomerName: string }) {
  const [newCustomerName, setNewCustomerName] = useState(defaultCustomerName);
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" style={{zIndex: 100}}>
      <div className="absolute inset-0" style={{ backgroundColor: "rgba(0,0,0,0.4)" }} onClick={onClose}></div>
      <div className="relative w-full sm:max-w-sm bg-white rounded-t-2xl sm:rounded-2xl px-5 pt-5 pb-8 z-10">
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1.5" style={{ color: "#374151" }}>
            변경할 고객명(기존 고객명: {defaultCustomerName})
          </label>
          <input
            value={newCustomerName}
            onChange={(e) => setNewCustomerName(e.target.value)}
            placeholder="고객명을 입력해주세요"
            maxLength={200}
            className="w-full rounded-xl px-3 py-2.5 text-sm resize-none outline-none"
            style={{ border: "1px solid #E5E7EB", color: "#111827", backgroundColor: "#FAFAFA" }}
          />
          
        </div>
        <div className="flex gap-2.5">
          <button
            onClick={onClose}
            className="flex-1 py-3.5 rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap"
            style={{ backgroundColor: "#F3F4F6", color: "#374151" }}
          >
            취소
          </button>
          <button
            onClick={() => onConfirm(newCustomerName)}
            className="flex-1 py-3.5 rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap"
            style={{ backgroundColor: "#2563EB", color: "#FFFFFF" }}
          >
            변경
          </button>
        </div>
      </div>
    </div>
  );
}
function ToastMessage({ message, onClose }: { message: string; onClose: () => void }) {
  return (
    <div
      className="fixed top-20 left-1/2 -translate-x-1/2 z-50 px-4 py-3 rounded-2xl flex items-center gap-2 whitespace-nowrap"
      style={{ backgroundColor: "#1E3A5F", color: "white" }}
    >
      <i className="ri-check-line text-sm" style={{ color: "#60A5FA" }}></i>
      <span className="text-sm font-medium">{message}</span>
      <button onClick={onClose} className="ml-2 cursor-pointer" style={{ color: "rgba(255,255,255,0.6)" }}>
        <i className="ri-close-line text-sm"></i>
      </button>
    </div>
  );
}

function CustomerCompactBar({ booking, openEditCustomerNameModal }: { booking: BookingDetailUI; openEditCustomerNameModal: () => void }) {
  
  return (
    <div
      className="bg-white rounded-2xl px-4 py-3 flex items-center gap-3"
      style={{ border: "1px solid #E5E7EB" }}
    >
      <div
        className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: "#EFF6FF" }}
      >
        <i className="ri-user-line text-sm" style={{ color: "#2563EB" }}></i>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold leading-tight flex items-center gap-1.5" style={{ color: "#111827" }}>
          {booking.customerName}
          <button onClick={openEditCustomerNameModal} className="w-5 h-5 flex items-center justify-center rounded-full font-normal hover:bg-gray-100 transition-colors" >
            <i className='ri-edit-line text-xl'></i>
          </button>
        </p>
        <p className="text-xs mt-0.5" style={{ color: "#6B7280" }}>
          {formatPhone(booking.customerPhone)}
        </p>
      </div>
      <div className="flex-shrink-0">
        <span className="text-xs" style={{ color: "#9CA3AF" }}>담당</span>
        <span className="text-xs font-semibold ml-1" style={{ color: "#374151" }}>
          {booking.plannerDisplayName} 플래너
        </span>
      </div>
    </div>
  );
}

function CancelInfoCard({ booking }: { booking: BookingDetailUI }) {
  if (booking.status !== "cancelled") return null;
  if (!booking.cancelledAt && !booking.cancelReason) return null;

  function formatDateTime(isoStr: string) {
    const date = new Date(isoStr);
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    const hh = String(date.getHours()).padStart(2, "0");
    const mm = String(date.getMinutes()).padStart(2, "0");
    return `${y}.${m}.${d} ${hh}:${mm}`;
  }

  return (
    <div className="bg-white rounded-2xl px-5 py-4" style={{ border: "1px solid #FECACA" }}>
      <div className="flex items-center gap-2 mb-3">
        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
          <i className="ri-close-circle-line text-xs" style={{ color: "#DC2626" }}></i>
        </div>
        <p className="text-sm font-bold" style={{ color: "#DC2626" }}>이 예약은 취소되었습니다</p>
      </div>
      <div className="space-y-2">
        {booking.cancelledAt && (
          <div className="flex items-center justify-between">
            <span className="text-xs" style={{ color: "#9CA3AF" }}>취소 시각</span>
            <span className="text-sm" style={{ color: "#374151" }}>{formatDateTime(booking.cancelledAt)}</span>
          </div>
        )}
        {booking.cancelReason && (
          <div className="flex items-center justify-between">
            <span className="text-xs" style={{ color: "#9CA3AF" }}>취소 사유</span>
            <span className="text-sm" style={{ color: "#374151" }}>{booking.cancelReason}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function MemoCard({ memo }: { memo: string }) {
  return (
    <div className="bg-white rounded-2xl px-5 py-4" style={{ border: "1px solid #E5E7EB" }}>
      <div className="flex items-center gap-2 mb-2">
        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
          <i className="ri-sticky-note-line text-xs" style={{ color: "#2563EB" }}></i>
        </div>
        <p className="text-sm font-bold" style={{ color: "#111827" }}>메모</p>
      </div>
      <p className="text-sm leading-relaxed" style={{ color: "#374151" }}>{memo}</p>
    </div>
  );
}

function minuteToTime(minuteOfDay: number) {
  const hour = Math.floor(minuteOfDay / 60);
  const minute = minuteOfDay % 60;
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

function mapReservationToViewModel(
  data: ReservationDetailResponse,
  shopMap: Record<number, ShopItem>
): BookingDetailUI {
  const reservation = data.reservation;
  const sortedItems = [...(data.items || [])].sort((a, b) => {
    if (a.slot.slotDate !== b.slot.slotDate) return a.slot.slotDate.localeCompare(b.slot.slotDate);
    if (a.slot.startMinuteOfDay !== b.slot.startMinuteOfDay) return a.slot.startMinuteOfDay - b.slot.startMinuteOfDay;
    return a.slot.id - b.slot.id;
  });

  const slots: BookingDetailSlot[] = sortedItems.map((item) => {
    const shop = shopMap[item.slot.shopId];

    return {
      id: item.slot.id,
      shopId: item.slot.shopId,
      shopName: shop?.shopName || `업체 #${item.slot.shopId}`,
      vendorName: shop?.shopName || `업체 #${item.slot.shopId}`,
      roomName: `룸 ${item.slot.roomId}`,
      address: shop?.address || "",
      lat: shop?.lat,
      lng: shop?.lng,
      phoneNumber: item.slot.phoneNumber,
      penaltyLink: item.slot.penaltyLink,
      naverPlaceId: item.slot.naverPlaceId,

      time: minuteToTime(item.slot.startMinuteOfDay),
      endTime: minuteToTime(item.slot.endMinuteOfDay),
      note: item.slot.memo || reservation.memo || null,
      fittingFee: undefined,
      feeStatus: undefined,
      penaltyInfo: null,
      phone: null,
    };
  });

  const firstSlotDate =
    slots.length > 0 ? sortedItems[0].slot.slotDate : reservation.reservedTime.slice(0, 10);

  return {
    code: reservation.reservationCode,
    status: reservation.reservationStatus === "CANCELED" ? "cancelled" : "confirmed",
    customerName: reservation.customerName,
    customerPhone: reservation.customerPhone,
    createdAt: reservation.reservedTime,
    cancelledAt: reservation.canceledTime,
    cancelReason: reservation.cancelReason,
    weddingDate: reservation.weddingDate,
    noShooting: reservation.noShooting,
    shootingDate: reservation.shootingDate,
    shootingDateUndecided: reservation.shootingDateUndecided,
    weddingDateUndecided: reservation.weddingDateUndecided,
    cancelReason: reservation.cancelReason,
    notice: reservation.notice,
    memo: reservation.memo,
    plannerName: "내 예약",
    date: firstSlotDate,
    slots,
  };
}

function BookingDetailContent({ 
  booking, 
  isConfirm, 
  loadReservation,
  handleCopyItinerary
}: { 
    booking: BookingDetailUI; 
    isConfirm: boolean;
    loadReservation: Function;
    handleCopyItinerary: Function;
  }) {
  const navigate = useNavigate();
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [currentBooking, setCurrentBooking] = useState<BookingDetailUI>(booking);
  const [activeSlotIndex, setActiveSlotIndex] = useState<number | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [showEditCustomerNameModal, setShowEditCustomerNameModal] = useState(false);

  const openEditCustomerNameModal = () => {
    setShowEditCustomerNameModal(true);
  };
  useEffect(() => {
    setCurrentBooking(booking);
  }, [booking]);

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 2500);
  };
  const handleCancel = async (reason: string) => {
    const { code, slots } = booking;

    const shopCount = new Set(slots.map(s => s.shopId)).size;
    const slotCount = slots.length;
    const selectedDate = slots[0]?.slotDate;

    // 1. submit
    try {
      trackEvent(GA_EVENT.RESV_CANCEL_SUBMIT, {
        reservation_code: code,
        shop_count: shopCount,
        slot_count: slotCount,
        selected_date: selectedDate,
        cancel_reason_code: reason || "UNKNOWN",
      });
    } catch (e) {

    }

    try {
      await cancelReservation({
        reservationCode: code,
        cancelReason: reason,
      });

      // 2. success
      try {
        trackEvent(GA_EVENT.RESV_CANCEL_SUCCESS, {
          reservation_code: code,
          shop_count: shopCount,
          slot_count: slotCount,
          selected_date: selectedDate,
          cancel_reason_code: reason || "UNKNOWN",
        });
      } catch (e) {
      
      }

      loadReservation();
      setShowCancelModal(false);
      showToast("예약이 취소되었습니다");

    } catch (error: any) {
      // 3. fail
      try {
        trackEvent(GA_EVENT.RESV_CANCEL_FAIL, {
          reservation_code: code,
          shop_count: shopCount,
          slot_count: slotCount,
          selected_date: selectedDate,
          cancel_reason_code: reason || "UNKNOWN",
          fail_reason: error?.response?.data?.message || "UNKNOWN_ERROR",
        });
      } catch (e) {

      }

      throw error;
    }
  };
  const handleChangeCustomerName = async (newName: string) => {
    const result = await changeCustomerName({ reservationCode: currentBooking.code, newCustomerName: newName })
    if (result.status !== 200) {
      alert(result.message || "고객명 변경에 실패했습니다");
      return;
    }
    loadReservation()
    setShowEditCustomerNameModal(false);
    showToast("고객명이 변경되었습니다");
  }
  const handleSlotSelect = (index: number) => {
    setActiveSlotIndex((prev) => (prev === index ? null : index));
  };

  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: `드레스 투어 예약 - ${currentBooking.customerName}`, url });
      } else {
        await navigator.clipboard.writeText(url);
        showToast("링크가 복사되었습니다");
      }
    } catch {
      // noop
    }
  };

  // const handleTextShare = async () => {
  //   const lines = [
  //     `📋 드레스 투어 예약 안내`,
  //     `예약코드: ${currentBooking.code}`,
  //     `고객명: ${currentBooking.customerName}`,
  //     `투어일: ${currentBooking.date}`,
  //     ``,
  //     ...currentBooking.slots.map(
  //       (s, i) => `${i + 1}. ${s.time}~${s.endTime} ${s.vendorName} (${s.address ?? ""})`
  //     ),
  //   ];

  //   try {
  //     await navigator.clipboard.writeText(lines.join("\n"));
  //     showToast("일정 텍스트가 복사되었습니다");
  //   } catch {
  //     showToast("텍스트 복사에 실패했습니다");
  //   }
  // };
  const openCancelModal = () => {
    try {
      trackEvent(GA_EVENT.RESV_CANCEL_CLICK, {
        reservation_code: currentBooking.code,
      });
    } catch (e) {

    }
    setShowCancelModal(true);
  }
  return (
    <div className="min-h-screen" style={{ backgroundColor: "#F5F6F8" }}>
      <BookingDetailHeader
        booking={currentBooking}
        isConfirm={isConfirm}
        onShare={handleShare}
        onTextShare={handleCopyItinerary}
      />
      
      {toast && <ToastMessage message={toast} onClose={() => setToast(null)} />}
      <div style={{padding: 16, paddingBottom: 8}}>
        <CancelInfoCard booking={currentBooking} />
      </div>
      <div className="max-w-screen-xl mx-auto px-4 lg:px-8 py-5 lg:py-6">
        <div className="flex flex-col lg:flex-row gap-5 lg:gap-6 items-start">
          <div className="w-full lg:w-[65%] space-y-4">
            <WeddingScheduleCard booking={currentBooking} />
            <CustomerCompactBar booking={currentBooking} openEditCustomerNameModal={openEditCustomerNameModal} />
            <DressTourTimeLine
              slots={currentBooking.slots}
              activeIndex={activeSlotIndex}
              onSlotSelect={handleSlotSelect}
              onShowToast={showToast}
            />
            <CardSection
              title="동선 지도"
              icon="ri-map-pin-line"
              right={
                <div className="flex items-center gap-2">
                  {/* <button
                    onClick={() => {}}
                    className="text-xs font-medium px-3 py-2 rounded-lg"
                    style={{ backgroundColor: "#F3F4F6", color: "#374151" }}
                  >
                    지도 공유
                  </button> */}
                </div>
              }
            >
              <RouteNaverMap
                slots={currentBooking.slots}
                activeIndex={activeSlotIndex}
                onSlotSelect={handleSlotSelect}
                connectLine
              />
              <div
                className="px-4 py-3 flex items-center gap-2 overflow-x-auto border-t"
                style={{ borderColor: "#F3F4F6" }}
              >
                {currentBooking.slots.map((slot, idx) => {
                  const isActive = activeSlotIndex === idx;
                  return (
                    <button
                      key={slot.id ?? idx}
                      onClick={() => handleSlotSelect(idx)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full flex-shrink-0 cursor-pointer whitespace-nowrap transition-all"
                      style={{
                        backgroundColor: isActive ? "#2563EB" : "#F3F4F6",
                        color: isActive ? "white" : "#374151",
                        border: isActive ? "1px solid #2563EB" : "1px solid transparent",
                      }}
                    >
                      <span
                        className="w-4 h-4 rounded-full flex items-center justify-center font-bold flex-shrink-0"
                        style={{
                          backgroundColor: isActive ? "rgba(255,255,255,0.25)" : "#E5E7EB",
                          color: isActive ? "white" : "#6B7280",
                          fontSize: "9px",
                        }}
                      >
                        {idx + 1}
                      </span>
                      <span className="text-xs font-medium">{slot.vendorName}</span>
                      <span className="text-xs" style={{ color: isActive ? "rgba(255,255,255,0.7)" : "#9CA3AF" }}>
                        {slot.time}
                      </span>
                    </button>
                  );
                })}
              </div>
            </CardSection>
            
            {currentBooking.memo && <MemoCard memo={currentBooking.memo} />}

            <div className="lg:hidden">
              <SummaryPanel
                booking={currentBooking}
                onCancel={openCancelModal}
                onShare={handleShare}
                onTextShare={handleCopyItinerary}
                openEditCustomerNameModal={openEditCustomerNameModal}
              />
            </div>

            <div className="h-24 lg:hidden"></div>
          </div>

          <div className="hidden lg:block lg:w-[35%] sticky top-20">
            <SummaryPanel
              booking={currentBooking}
              onCancel={openCancelModal}
              onShare={handleShare}
              onTextShare={handleCopyItinerary}
              openEditCustomerNameModal={openEditCustomerNameModal}
            />
          </div>
        </div>
      </div>

      {currentBooking.status === "confirmed" && (
        <div
          className="fixed bottom-0 left-0 right-0 bg-white border-t px-4 py-3 lg:hidden z-20"
          style={{ borderColor: "#E5E7EB" }}
        >
          <div className="flex gap-2.5">
            <button
              onClick={() => setShowCancelModal(true)}
              className="py-3.5 px-4 rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors"
              style={{ backgroundColor: "#FEF2F2", color: "#DC2626" }}
            >
              예약 취소
            </button>
            <button
              onClick={() => navigate("/reservations")}
              className="flex-1 py-3.5 rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors"
              style={{ backgroundColor: "#F3F4F6", color: "#374151" }}
            >
              예약 내역
            </button>
            {/* <button
              onClick={handleShare}
              className="flex-1 py-3.5 rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors"
              style={{ backgroundColor: "#2563EB", color: "#FFFFFF" }}
            >
              <i className="ri-share-line mr-1.5"></i>
              공유하기
            </button> */}
          </div>
        </div>
      )}
      {
        showEditCustomerNameModal && (
          <NameEditModal
            defaultCustomerName={currentBooking.customerName}
            onConfirm={handleChangeCustomerName} 
            onClose={() => setShowEditCustomerNameModal(false)} 
          />
        )
      }
      {showCancelModal && (
        <CancelModal onConfirm={handleCancel} onClose={() => setShowCancelModal(false)} />
      )}
    </div>
  );
}

export default function BookingDetailPage() {
  const { reservationCode } = useParams<{ reservationCode: string }>();
  const [searchParams] = useSearchParams();
  const isConfirm = searchParams.get("confirm") === "true";
  const authUser = useSelector((state: any) => state.auth.user);
  const plannerDisplayName = authUser?.userName
  const shops = useSelector((state: any) => state.shop?.shops || []);
  const shopMap = useMemo(() => {
    return (shops || [])
      .map((v: any) => ({ ...v, lng: v.lon }))
      .reduce((acc: Record<number, ShopItem>, shop: ShopItem) => {
        acc[Number(shop.id)] = shop;
        return acc;
      }, {});
  }, [shops]);

  const [reservationData, setReservationData] = useState<ReservationDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  async function loadReservation() {
    if (!reservationCode) {
      setNotFound(true);
      setLoading(false);
      return;
    }
 
    try {
      setLoading(true);
      setNotFound(false);

      const result = await fetchReservation(reservationCode);


      const data = result?.data?.data || result?.data || result;
      setReservationData(data || null);
    } catch {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    
    

    loadReservation();

    return 
  }, [reservationCode]);

  const booking = useMemo(() => {
    if (!reservationData) return null;
    return {...mapReservationToViewModel(reservationData, shopMap), plannerName: plannerDisplayName};
  }, [reservationData, shopMap]);
  const onClickTextCopy = async () => {
    if (!reservationData?.items?.length) return;

    const noticeText = buildDressTourNoticeText({
      slots: reservationData.items.map((item) => ({
        slotDate: item.slot.slotDate,
        startMinuteOfDay: item.slot.startMinuteOfDay,
        shopName: shopMap[item.slot.shopId]?.shopName || `업체 #${item.slot.shopId}`,
      })),
    });

    try {
      await copyToClipboard(noticeText);
    } catch (error) {
      console.error(error);
    }
  };
  onClickTextCopy()
  // if (loading) return <LoadingState />;
  if (notFound || !booking) return <NotFoundState />;

  return <BookingDetailContent 
    booking={booking} 
    isConfirm={isConfirm} 
    loadReservation={loadReservation} 
    handleCopyItinerary={onClickTextCopy}
  />;
}